import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { submitSmartDeviceToVista } from "../../../../api/vistaOcrService";
import arrowForward from "../../../../assets/images/arrow_forward_button.svg";
import Button from "../../../../components/common/Button/Button";
import SummaryContent from "../../../../components/common/SummaryContent/SummaryContent";
import TravelDetailsTable from "../../../../components/common/TravelDetailsTable/TravelDetailsTable";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePersonalAccidentFlow from "../../../../hooks/usePersonalAccidentFlow";
import usePersonalCyberFlow from "../../../../hooks/usePersonalCyberFlow";
import useSmartDeviceFlow from "../../../../hooks/useSmartDeviceFlow";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import {
  fetchOccupationsList,
  selectOccupations,
} from "../../../../redux/features/constants/occupationsListSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { policyPay } from "../../../../redux/features/payment/policyPaySlice";
import { selectPersonalAccidentForm } from "../../../../redux/features/personalAccidentFormSlice";
import { selectPersonalCyberForm } from "../../../../redux/features/personalCyberFormSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import {
  selectPersonalCyberQuote,
  selectSmartDeviceQuote,
} from "../../../../redux/features/quoteSlice";
import { selectSmartDeviceForm } from "../../../../redux/features/smartDeviceFormSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import {
  applyPromoCodeToQuote,
  calculatePromoDiscount,
  getFinalQuoteNumberForPayment,
  getFinalPremiumForPayment,
} from "../../../../utils/promoCodeUtils";
import PersonalCyberJourneyLayout from "../../../newPersonalCyberPolicy/layouts/PersonalCyberJourneyLayout";
import SmartDeviceJourneyLayout from "../../../newSmartDevicePolicy/layouts/SmartDeviceJourneyLayout";
import PersonalAccidentJourneyLayout from "../../layouts/PersonalAccidentJourneyLayout";
import { PERSONAL_ACCIDENT_CONTACT_DETAILS_FORM } from "../contactDetails/contactDetails";

// Define the form constant locally since it's not exported from personalInfo
const PERSONAL_ACCIDENT_BASIC_INFO = "PERSONAL_ACCIDENT_BASIC_INFO";

// Import cyber insurance form constants
const PERSONAL_CYBER_CONTACT_DETAILS_FORM =
  "PERSONAL_CYBER_CONTACT_DETAILS_FORM";
const PERSONAL_CYBER_SUM_INSURED_FORM = "PERSONAL_CYBER_SUM_INSURED_FORM";

// Import smart device insurance form constants
const SMART_DEVICE_CONTACT_DETAILS_FORM = "SMART_DEVICE_CONTACT_DETAILS_FORM";
const SMART_DEVICE_SELECTION_FORM = "SMART_DEVICE_SELECTION_FORM";
const SMART_DEVICE_PURCHASE_DETAILS_FORM = "SMART_DEVICE_PURCHASE_DETAILS_FORM";

const PaymentSummary = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isCyberInsurance =
    location.pathname.includes("personal-cyber") ||
    location.pathname.includes("new-personal-cyber");
  const isSmartDevice = location.pathname.includes("smart-device");
  const dispatch = useDispatch();

  console.log("=== PaymentSummary Debug ===");
  console.log("Current pathname:", location.pathname);
  console.log("isCyberInsurance:", isCyberInsurance);
  console.log("isSmartDevice:", isSmartDevice);

  // Always use English numerals, detect RTL for currency positioning
  const isRTL = i18n.resolvedLanguage === "ar";

  // Use appropriate form data and hooks based on insurance type
  const accidentFormData = useSelector(selectPersonalAccidentForm);
  const cyberFormData = useSelector(selectPersonalCyberForm);
  const smartDeviceFormData = useSelector(selectSmartDeviceForm);
  const formData = isSmartDevice
    ? smartDeviceFormData
    : isCyberInsurance
    ? cyberFormData
    : accidentFormData;

  // Get quote data from API
  const personalCyberQuote = useSelector(selectPersonalCyberQuote);
  const smartDeviceQuote = useSelector(selectSmartDeviceQuote);

  const { handleNextSubstep: handleAccidentNext } = usePersonalAccidentFlow();
  const { handleNextSubstep: handleCyberNext } = usePersonalCyberFlow();
  const { handleNextSubstep: handleSmartDeviceNext } = useSmartDeviceFlow();
  const handleNextSubstep = isSmartDevice
    ? handleSmartDeviceNext
    : isCyberInsurance
    ? handleCyberNext
    : handleAccidentNext;

  const translate = useLanguageTranslate();

  // Get occupations and nationalities data from Redux store
  const occupationsData = useSelector(selectOccupations);
  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);

  // Get profile data for logged-in users
  const profileData = useSelector(selectProfileData);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);

  // Fetch occupations and nationalities data on component mount
  useEffect(() => {
    if (!occupationsData && !isCyberInsurance && !isSmartDevice) {
      dispatch(fetchOccupationsList());
    }
    // Always fetch nationalities for cyber insurance (for nationality code conversion)
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [
    dispatch,
    occupationsData,
    isCyberInsurance,
    isSmartDevice,
    nationalityOptions.length,
    nationalitiesLoading,
  ]);

  // Helper function to get nationality name from code
  const getNationalityName = (nationalityCode) => {
    console.log("=== PaymentSummary getNationalityName Debug ===");
    console.log(
      "Input nationalityCode:",
      nationalityCode,
      "Type:",
      typeof nationalityCode
    );
    console.log("nationalityOptions length:", nationalityOptions?.length);

    // Return the raw code if nationality options aren't loaded yet
    if (!nationalityOptions || nationalityOptions.length === 0) {
      console.log(
        "Nationality options not loaded yet, returning raw code:",
        nationalityCode
      );
      return nationalityCode; // Return raw code, don't guess
    }

    if (!nationalityCode && nationalityCode !== 0) {
      console.log("No nationality code provided, returning null");
      return null;
    }

    // If it's already a string and not a number-like string, return it
    if (typeof nationalityCode === "string" && isNaN(nationalityCode)) {
      console.log(
        "Nationality code is already a string name:",
        nationalityCode
      );
      return nationalityCode;
    }

    // Convert string numbers to actual numbers for lookup
    const codeToLookup =
      typeof nationalityCode === "string"
        ? parseInt(nationalityCode)
        : nationalityCode;
    console.log("Code to lookup:", codeToLookup);

    // Find the nationality name by code - exact match on code field
    const found = nationalityOptions.find((opt) => opt.code === codeToLookup);
    console.log("Found nationality object:", found);

    if (found) {
      // Use 'label' field as primary, 'value' as fallback
      const result = found.label || found.value;
      console.log("Successfully found nationality:", result);
      console.log("=== End PaymentSummary getNationalityName Debug ===");
      return result;
    }

    console.log("No nationality found for code:", codeToLookup);
    console.log("=== End PaymentSummary getNationalityName Debug ===");
    return nationalityCode; // Return original code if lookup fails
  };

  // Helper function to get nationality name in Arabic
  const getNationalityInArabic = (englishName) => {
    const nationalityTranslations = {
      bahraini: "بحريني",
      saudi: "سعودي",
      emirati: "إماراتي",
      kuwaiti: "كويتي",
      qatari: "قطري",
      omani: "عماني",
      egyptian: "مصري",
      jordanian: "أردني",
      lebanese: "لبناني",
      syrian: "سوري",
      iraqi: "عراقي",
      palestinian: "فلسطيني",
      yemeni: "يمني",
      sudanese: "سوداني",
      moroccan: "مغربي",
      tunisian: "تونسي",
      algerian: "جزائري",
      libyan: "ليبي",
      indian: "هندي",
      pakistani: "باكستاني",
      bangladeshi: "بنغلاديشي",
      filipino: "فلبيني",
      indonesian: "إندونيسي",
      "sri lankan": "سريلانكي",
      nepalese: "نيبالي",
      american: "أمريكي",
      british: "بريطاني",
      canadian: "كندي",
      australian: "أسترالي",
      german: "ألماني",
      french: "فرنسي",
      italian: "إيطالي",
      spanish: "إسباني",
      russian: "روسي",
      chinese: "صيني",
      japanese: "ياباني",
      korean: "كوري",
      turkish: "تركي",
      iranian: "إيراني",
    };

    // Make case-insensitive lookup
    const lowerName = englishName?.toLowerCase();
    return nationalityTranslations[lowerName] || englishName;
  };

  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Get data from Redux store based on insurance type
  let contactDetailsData, basicInfoData, planData, deviceData, purchaseData;

  if (isSmartDevice) {
    contactDetailsData = formData[SMART_DEVICE_CONTACT_DETAILS_FORM] || {};
    deviceData = formData[SMART_DEVICE_SELECTION_FORM] || {};
    purchaseData = formData[SMART_DEVICE_PURCHASE_DETAILS_FORM] || {};

    // Use real API data from quote
    // CRITICAL: When promo is applied, use values from promo API response
    planData = {
      premium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Premium
        : smartDeviceQuote?.premium || 0,
      tax: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Tax
        : smartDeviceQuote?.tax || 0,
      totalPremium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.TotalPremium
        : smartDeviceQuote?.totalPremium || 0,
      quoteNumber:
        smartDeviceQuote?.quoteNumber ||
        smartDeviceQuote?.policyNumber ||
        "N/A",
      basePremium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Premium
        : smartDeviceQuote?.premium || 0,
    };
  } else if (isCyberInsurance) {
    contactDetailsData = formData[PERSONAL_CYBER_CONTACT_DETAILS_FORM] || {};

    console.log("=== Personal Cyber Form Data Debug ===");
    console.log("formData:", formData);
    console.log("contactDetailsData:", contactDetailsData);
    console.log(
      "contactDetailsData.passengers:",
      contactDetailsData?.passengers
    );

    // Use real API data from quote
    // CRITICAL: When promo is applied, use values from promo API response
    planData = {
      premium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Premium
        : personalCyberQuote?.premium || 0,
      tax: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Tax
        : personalCyberQuote?.tax || 0,
      totalPremium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.TotalPremium
        : personalCyberQuote?.totalPremium || 0,
      quoteNumber:
        personalCyberQuote?.quoteNumber ||
        personalCyberQuote?.policyNumber ||
        "N/A",
      basePremium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Premium
        : personalCyberQuote?.premium || 0,
    };
  } else {
    basicInfoData = formData[PERSONAL_ACCIDENT_BASIC_INFO] || {};
    contactDetailsData = formData[PERSONAL_ACCIDENT_CONTACT_DETAILS_FORM] || {};

    // Get quote data from API (saved in contactDetails page)
    const quoteData = formData["PERSONAL_ACCIDENT_QUOTE_DATA"];

    // Use API data directly - no calculations
    // CRITICAL: When promo is applied, use values from promo API response
    planData = {
      premium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Premium
        : quoteData?.data?.premium || 0,
      tax: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Tax
        : quoteData?.data?.tax || 0,
      totalPremium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.TotalPremium
        : quoteData?.data?.totalPremium || 0,
      quoteNumber: quoteData?.data?.quoteNumber || "N/A",
      basePremium: promoCodeApplied && promoAppliedQuoteData
        ? promoAppliedQuoteData.Premium
        : quoteData?.data?.premium || 0,
    };
  }

  // Extract passenger data
  const passengers = contactDetailsData?.passengers || [];

  // Build table rows for insurance data
  const buildInsuranceData = () => {
    const data = {};

    if (isSmartDevice) {
      // Smart Device Insurance specific fields
      // Add passenger/policyholder name
      if (passengers.length > 0 && passengers[0]?.fullName) {
        data[translate("Full Name", "الاسم الكامل")] = passengers[0].fullName;
      }

      // Add CPR number
      if (passengers.length > 0 && passengers[0]?.cprNumber) {
        data[translate("CPR", "رقم الهوية الوطنية")] =
          passengers[0].cprNumber;
      }

      // Add Device IMEI (if available)
      if (deviceData?.imeiNumber) {
        data[translate("Device's IMEI", "رقم IMEI")] = deviceData.imeiNumber;
      }

      // Add Brand
      if (deviceData?.brand) {
        data[translate("Brand", "العلامة تجارية (Brand)")] = deviceData.brand;
      }

      // Add Model
      if (deviceData?.model) {
        data[translate("Model", "الطراز (Model)")] = deviceData.model;
      }

      // Add Device Price
      if (purchaseData?.devicePrice) {
        const currency = translate("BHD", "د.ب");
        const formattedPrice = formatPremiumWithCurrency(
          parseFloat(purchaseData.devicePrice),
          "",
          "en-US"
        );
        // For RTL, reverse the order
        data[translate("Device Price", "سعر الجهاز")] = isRTL
          ? `${formattedPrice} ${currency}`
          : `${currency} ${formattedPrice}`;
      }

      // Add Purchase Date
      if (purchaseData?.purchaseDate) {
        const formatDate = (dateString) => {
          try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          } catch {
            return dateString;
          }
        };
        data[translate("Purchase date", "تاريخ الشراء")] = formatDate(
          purchaseData.purchaseDate
        );
      }

      return data;
    }

    if (isCyberInsurance) {
      // Add passenger/policyholder name for Cyber Insurance
      if (passengers.length > 0 && passengers[0]?.fullName) {
        data[translate("Full Name", "الاسم الكامل")] = passengers[0].fullName;
      }

      // Add CPR number for Cyber Insurance
      if (passengers.length > 0 && passengers[0]?.cprNumber) {
        data[translate("CPR", "رقم الهوية الوطنية")] = passengers[0].cprNumber;
      }
      console.log("=== Personal Cyber PaymentSummary Debug ===");
      console.log("isCyberInsurance:", isCyberInsurance);
      console.log("passengers:", passengers);
      console.log("passengers[0]:", passengers[0]);
      console.log("passengers[0]?.nationality:", passengers[0]?.nationality);

      // Nationality field removed for Personal Cyber insurance

      // Add Sum Insured Limit for Personal Cyber
      console.log("=== Personal Cyber Sum Insured Debug ===");
      console.log("personalCyberQuote:", personalCyberQuote);
      console.log(
        "personalCyberQuote?.sumInsured:",
        personalCyberQuote?.sumInsured
      );
      console.log("=== End Sum Insured Debug ===");

      if (personalCyberQuote?.sumInsured) {
        const currency = translate("BHD", "د.ب");
        const formattedSumInsured = formatPremiumWithCurrency(
          parseFloat(personalCyberQuote.sumInsured),
          "",
          "en-US"
        );
        // For RTL, reverse the order (amount currency)
        data[translate("Sum Insured Limit", "مبلغ التأمين")] = isRTL
          ? `${formattedSumInsured} ${currency}`
          : `${currency} ${formattedSumInsured}`;
      } else {
        // Fallback: Check if sum insured is available in form data
        const sumInsuredData = formData[PERSONAL_CYBER_SUM_INSURED_FORM];
        if (sumInsuredData?.selectedSumInsured) {
          console.log(
            "Using fallback sum insured from form data:",
            sumInsuredData.selectedSumInsured
          );
          const currency = translate("BHD", "د.ب");
          const formattedSumInsured = formatPremiumWithCurrency(
            parseFloat(sumInsuredData.selectedSumInsured),
            "",
            "en-US"
          );
          // For RTL, reverse the order (amount currency)
          data[translate("Sum Insured Limit", "مبلغ التأمين")] = isRTL
            ? `${formattedSumInsured} ${currency}`
            : `${currency} ${formattedSumInsured}`;
        }
      }

      // Gender field removed for Personal Cyber insurance
    } else {
      // Personal Accident Insurance - Show: Full Name, CPR, Occupation, Date of Birth, Policy Limit

      // Debug: Log available data for Personal Accident
      console.log("=== Personal Accident Review Data Debug ===");
      console.log("Is logged in:", !!loginTokenInfo);
      console.log("basicInfoData:", basicInfoData);
      console.log("contactDetailsData:", contactDetailsData);
      console.log("passengers:", passengers);
      console.log("profileData:", profileData);
      console.log(
        "Priority: 1) Contact details passenger, 2) Profile data (logged-in), 3) Basic info"
      );
      console.log("=== End Personal Accident Debug ===");

      // Personal Accident specific fields in the required order:
      // 1. Full Name, 2. CPR, 3. Occupation, 4. Date of Birth, 5. Policy Limit

      // 1. Add passenger/policyholder name for Personal Accident
      // Priority: 1) Contact details passenger, 2) Profile data (logged-in), 3) Basic info
      let fullName = "";
      if (passengers.length > 0 && passengers[0]?.fullName) {
        fullName = passengers[0].fullName;
      } else if (
        loginTokenInfo &&
        profileData?.FirstName &&
        profileData?.LastName
      ) {
        // Priority to profile data for logged-in users
        fullName = `${profileData.FirstName} ${profileData.LastName}`.trim();
      } else if (basicInfoData?.fullName) {
        // Fallback to basic info data for guests
        fullName = basicInfoData.fullName;
      }
      if (fullName) {
        data[translate("Full Name", "الاسم الكامل")] = fullName;
      }

      // 2. Add CPR number for Personal Accident
      // Priority: 1) Contact details passenger, 2) Profile data (logged-in), 3) Basic info
      let cprNumber = "";
      if (passengers.length > 0 && passengers[0]?.cprNumber) {
        cprNumber = passengers[0].cprNumber;
      } else if (loginTokenInfo && profileData?.CPR) {
        // Priority to profile data for logged-in users
        cprNumber = profileData.CPR;
      } else if (basicInfoData?.cprNumber) {
        // Fallback to basic info data for guests
        cprNumber = basicInfoData.cprNumber;
      }
      if (cprNumber) {
        data[translate("CPR", "رقم الهوية الوطنية")] = cprNumber;
      }

      // 3. Add occupation
      if (basicInfoData?.occupation) {
        // Find the occupation name from the occupations list
        let occupationName = basicInfoData.occupation;
        if (occupationsData && Array.isArray(occupationsData)) {
          console.log("Looking for occupation code:", basicInfoData.occupation);
          console.log("Occupations data sample:", occupationsData.slice(0, 2));
          const occupation = occupationsData.find(
            (occ) => String(occ.Code) === String(basicInfoData.occupation)
          );
          console.log("Found occupation:", occupation);
          if (occupation) {
            occupationName = translate(occupation.Name, occupation.NameAr);
          }
        }
        data[translate("Occupation", "المهنة")] = occupationName;
      }

      // 4. Add Date of Birth for Personal Accident (always show, even if empty)
      // Priority: 1) Contact details passenger, 2) Profile data (logged-in), 3) Basic info
      let dateOfBirth = "";
      if (passengers.length > 0 && passengers[0]?.dateOfBirth) {
        // Check different date formats from passenger data
        let dateValue = passengers[0].dateOfBirth;

        // Handle object format {day, month, year, fullDate}
        if (typeof dateValue === "object") {
          // Use fullDate if available
          if (dateValue.fullDate) {
            dateValue = dateValue.fullDate;
          }
          // Otherwise, construct from day, month, year
          else if (dateValue.day && dateValue.month && dateValue.year) {
            dateValue = `${dateValue.year}-${dateValue.month.padStart(2, "0")}-${dateValue.day.padStart(2, "0")}`;
          }
        }

        // Format the date if available
        try {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            dateOfBirth = date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
          }
        } catch (error) {
          console.log("Error formatting passenger date:", error);
        }
      } else if (
        loginTokenInfo &&
        (profileData?.DateOfBirth || profileData?.DOB)
      ) {
        // Priority to profile data for logged-in users
        try {
          const date = new Date(profileData.DateOfBirth || profileData.DOB);
          if (!isNaN(date.getTime())) {
            dateOfBirth = date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
          }
        } catch (error) {
          console.log("Error formatting profile date:", error);
        }
      } else if (basicInfoData?.birthDate) {
        // Fallback to basic info birth date for guests
        try {
          const date = new Date(basicInfoData.birthDate);
          if (!isNaN(date.getTime())) {
            dateOfBirth = date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
          }
        } catch (error) {
          console.log("Error formatting basic info date:", error);
        }
      }
      data[translate("Date of Birth", "تاريخ الميلاد")] = dateOfBirth || "";

      // 5. Add policy limit
      if (basicInfoData?.policy_limit) {
        const currency = translate("BHD", "د.ب");
        const formattedLimit = formatPremiumWithCurrency(
          parseFloat(basicInfoData.policy_limit),
          "",
          "en-US"
        );
        // For RTL, reverse the order (number first, currency after)
        data[translate("Policy Limit", "مبلغ الوثيقة")] = isRTL
          ? `${formattedLimit} ${currency}`
          : `${currency} ${formattedLimit}`;
      }
    }

    return data;
  };

  const insuranceData = buildInsuranceData();

  const applyPromoCode = async () => {
    setIsApplyingPromo(true);

    try {
      const quoteNumber = planData?.quoteNumber;
      const result = await applyPromoCodeToQuote(
        quoteNumber,
        promoCode,
        translate
      );

      if (result.success) {
        // Get original premium based on insurance type
        const originalPremium = isSmartDevice
          ? smartDeviceQuote?.totalPremium || 0
          : isCyberInsurance
          ? personalCyberQuote?.totalPremium || 0
          : formData["PERSONAL_ACCIDENT_QUOTE_DATA"]?.data?.totalPremium || 0;

        const discount = calculatePromoDiscount(
          originalPremium,
          result.data.TotalPremium
        );
        setPromoCodeApplied(true);
        setPromoCodeDiscount(discount);
        setPromoAppliedQuoteData(result.data);
        toast.success(
          translate("Promo code applied!", "تم تطبيق كود الخصم!")
        );
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error("Promo code application exception:", error);
      toast.error(
        translate(
          "Failed to apply promo code. Please try again.",
          "فشل تطبيق كود الخصم. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null);
  };
  const handleProceedToPayment = async () => {
    if (!agreedToTerms) {
      alert(
        translate(
          "Please agree to the terms and conditions",
          "يرجى الموافقة على الشروط والأحكام"
        )
      );
      return;
    }

    // Validate we have the necessary payment data
    const quoteNumber = planData?.quoteNumber;
    const totalPremium = planData?.totalPremium;

    if (!quoteNumber || !totalPremium || quoteNumber === "N/A") {
      toast.error(
        translate(
          "Unable to process payment. Quote information is missing.",
          "غير قادر على معالجة الدفع. معلومات التسعيرة مفقودة."
        )
      );
      console.error("Missing payment data:", { quoteNumber, totalPremium });
      return;
    }

    // CRITICAL: Set redirect flag IMMEDIATELY to prevent screen flash during payment processing
    sessionStorage.setItem("isRedirectingToPayment", "true");
    console.log("🔒 Set payment redirect flag - preventing flow reset during payment");

    // Get final quote number and premium (use promo values if applied)
    const finalQuoteNumber = getFinalQuoteNumberForPayment(
      promoCodeApplied,
      promoAppliedQuoteData,
      quoteNumber
    );
    const finalPremium = getFinalPremiumForPayment(
      promoCodeApplied,
      promoAppliedQuoteData,
      totalPremium
    );

    console.log("💰 Payment Data:", {
      promoCodeApplied,
      originalQuoteNumber: quoteNumber,
      finalQuoteNumber,
      originalPremium: totalPremium,
      finalPremium,
    });

    const payload = {
      PolicyNumber: finalQuoteNumber,
      Premium: finalPremium,
      Channel: getClientSource(),
    };

    setIsLoading(true);

    try {
      // For Smart Device, submit documents to Vista before payment
      if (isSmartDevice) {
        const customerName = passengers[0]?.fullName || "";
        console.log("[Vista] Submitting Smart Device documents before payment");

        const vistaResult = await submitSmartDeviceToVista({
          customerName,
          policyNumber: quoteNumber,
        });

        if (!vistaResult.success) {
          console.warn(
            "[Vista] Smart Device submission warning:",
            vistaResult.error
          );
          // Don't block payment - Vista submission is supplementary
          // Just log the warning and continue
        } else {
          console.log("[Vista] Smart Device documents submitted successfully");
        }
      }

      console.log("Initiating payment with payload:", payload);

      // Call the payment API
      const linkAction = await dispatch(policyPay(payload));
      const link = unwrapResult(linkAction);

      console.log("Payment link received:", link);

      if (link) {
        // Flag already set at the beginning - now just redirect
        console.log("🔄 Redirecting to payment gateway...");

        // Small delay to ensure state is saved, then redirect
        setTimeout(() => {
          window.location.href = link;
        }, 300);
      } else {
        // Clear flag on error
        sessionStorage.removeItem("isRedirectingToPayment");

        toast.error(
          translate(
            "Unable to generate payment link. Please try again.",
            "غير قادر على إنشاء رابط الدفع. يرجى المحاولة مرة أخرى."
          )
        );
      }
    } catch (error) {
      console.error("Payment initiation error:", error);

      // Clear flag on error
      sessionStorage.removeItem("isRedirectingToPayment");

      toast.error(
        translate(
          "Payment failed. Please try again.",
          "فشل الدفع. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const LayoutComponent = isSmartDevice
    ? SmartDeviceJourneyLayout
    : isCyberInsurance
    ? PersonalCyberJourneyLayout
    : PersonalAccidentJourneyLayout;

  // For Personal Cyber, render with inline button like Travel insurance
  if (isCyberInsurance) {
    return (
      <div>
        <LayoutComponent is_hide_back_btn={false}>
          <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 py-5 w-full max767:pb-[0px]">
            <div className="max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0 w-full max-w-2xl">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-semibold text-[#1A2024] mb-2 max767:text-left max767:rtl:text-right">
                  {translate("Review and Confirm", "المراجعة والتأكيد")}
                </h1>
                <p className="text-[#1A2024] max767:text-left max767:rtl:text-right">
                  {translate(
                    "Please review your information and proceed to payment.",
                    "يرجى مراجعة معلوماتك والانتقال إلى الدفع."
                  )}
                </p>
              </div>

              {/* Policy Section */}
              <div className="mb-8">
                {/* Insurance Details Table */}
                <div className="bg-[#F6F8F9] rounded-xl p-4 mb-6 max767:p-0 max767:bg-transparent">
                  <h2 className="text-base font-semibold text-[#1A2024] mb-2">
                    {translate(
                      "Personal Cyber Insurance",
                      "التأمين السيبراني الشخصي"
                    )}
                  </h2>
                  <p className="text-sm mb-4 text-[#1A2024]">
                    {translate(
                      "Review the details below and confirm the payment",
                      "قم بمراجعة التفاصيل أدناه وتأكيد الدفع"
                    )}
                  </p>
                  <TravelDetailsTable travelData={insuranceData} />
                </div>
              </div>

              {/* Summary Section */}
              <div className="mb-8">
                <div className="bg-[#F6F8F9] rounded-xl p-4">
                  <SummaryContent
                    planData={planData}
                    promoCode={promoCode}
                    setPromoCode={setPromoCode}
                    applyPromoCode={applyPromoCode}
                    clearPromoCode={clearPromoCode}
                    promoCodeApplied={promoCodeApplied}
                    promoCodeDiscount={promoCodeDiscount}
                    isApplyingPromo={isApplyingPromo}
                    showQuotationNumber={true}
                    showPromoCode={true}
                    className="!bg-transparent"
                    flowType="personalCyber"
                  />
                </div>
              </div>

              {/* Policy Notice */}
              <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="w-5 h-5 text-[#A56213]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-[#252C32] font-semibold text-sm">
                    {translate(
                      "Kindly note that the policy will be issued under your name",
                      "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
                    )}
                  </p>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mb-6">
                <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
                  <label class="flex items-center cursor-pointer gap-2">
                    <input
                      type="checkbox"
                      class="peer hidden"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                    />
                    <div
                      class="size-5 border-2 border-gray-400 rounded-[4px] 
              peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7] 
              flex items-center justify-center transition-all"
                    >
                      <svg
                        width="12"
                        height="9"
                        viewBox="0 0 12 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </label>
                  {/* <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 size-5 text-[#00A9E7] bg-gray-100 border-gray-300 rounded focus:ring-[#00A9E7] focus:ring-2"
                  /> */}
                  <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px] text-justify">
                    <span>
                      {translate(
                        "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                        "أقر بموجب هذا بأن المعلومات المذكورة أعلاه صحيحة ودقيقة على حد علمي، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إلغاء وثيقة التأمين الخاصة بي. كما قرأت وأوافق على"
                      )}
                    </span>{" "}
                    <button
                      className="text-[#00384D] underline font-bold hover:text-[#0088CC]"
                      onClick={() =>
                        window.open(
                          isCyberInsurance
                            ? "https://content.solidarity.bh/uploads/Personal_Cyber_Insurance_0fcdb73314.pdf"
                            : "https://content.solidarity.bh/uploads/TRAVEL_INSURANCE_WORDING_30_08_2023_8e74afd369_44e9596879.pdf",
                          "_blank"
                        )
                      }
                    >
                      {translate("Policy Specification", "مواصفات وثيقة")}
                    </button>{" "}
                    <span>
                      {translate("of this insurance product.", "التأمين هذه.")}
                    </span>
                  </div>
                </label>
              </div>

              {/* Proceed to Payment Button - Desktop */}
              <div className="max767:hidden">
                <Button
                  onSubmit={handleProceedToPayment}
                  disabled={!agreedToTerms || isLoading}
                  isLoading={isLoading}
                  label={translate("Proceed to Payment", "انتقل إلى الدفع")}
                  post_icon={arrowForward}
                  post_icon_class={isRTL ? "rotate-180 gap-2" : ""}
                  className="w-full mt-6 py-4 px-6 rounded-[10px] gap-2"
                />
              </div>
            </div>
          </div>

          {/* Proceed to Payment Button - Mobile (Fixed at Bottom) */}
          <div className="hidden max767:block fixed bottom-0 left-0 w-full bg-white border-t border-[#E5E9EB] p-4 pb-6 z-[9] max767:pb-[max(1rem,env(safe-area-inset-bottom))]">
            <Button
              onSubmit={handleProceedToPayment}
              disabled={!agreedToTerms || isLoading}
              isLoading={isLoading}
              label={translate("Proceed to Payment", "انتقل إلى الدفع")}
              post_icon={arrowForward}
              post_icon_class={isRTL ? "rotate-180 gap-2" : ""}
              className="w-full py-4 px-6 rounded-[10px] max767:h-12"
              fontSize="text-sm"
            />
          </div>
        </LayoutComponent>
      </div>
    );
  }

  // For Personal Accident and Smart Device, render with inline button for consistency
  return (
    <div>
      <LayoutComponent is_hide_back_btn={false}>
        <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 py-5 w-full pb-0 max767:pb-0">
          <div className="max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0 w-full max-w-2xl">
            {/* Header */}
            <div className="text-center max767:text-left mb-8">
              <h1 className="text-2xl font-semibold text-[#1A2024] mb-2 max767:rtl:text-right">
                {translate("Review and Confirm", "المراجعة والدفع")}
              </h1>
              <p className="text-[#1A2024] max767:rtl:text-right">
                {translate(
                  "Please review your information and proceed to payment.",
                  "يرجى مراجعة معلوماتك والانتقال إلى الدفع."
                )}
              </p>
            </div>

            {/* Policy Section */}
            <div className="mb-8">
              {/* Insurance Details Table */}
              <div className="bg-[#F6F8F9] max767:bg-white rounded-xl p-4 max767:p-0 mb-6">
                <h2 className="text-base font-semibold text-[#1A2024] mb-2">
                  {isSmartDevice
                    ? translate(
                        "Smart Device Insurance",
                        "التأمين على الأجهزة الذكية"
                      )
                    : isCyberInsurance
                    ? translate(
                        "Personal Cyber Insurance",
                        "التأمين السيبراني الشخصي"
                      )
                    : translate(
                        "Personal Accident Insurance",
                        "التأمين على الحوادث الشخصية"
                      )}
                </h2>
                <p className="text-sm mb-4 text-[#1A2024]">
                  {translate(
                    "Review the details below and confirm the payment",
                    "راجع التفاصيل أدناه وقم بتأكيد الدفع"
                  )}
                </p>
                <TravelDetailsTable travelData={insuranceData} />
              </div>
            </div>

            {/* Summary Section */}
            <div className="mb-8">
              <div className="bg-[#F6F8F9] rounded-xl p-4">
                <SummaryContent
                  planData={planData}
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  applyPromoCode={applyPromoCode}
                  clearPromoCode={clearPromoCode}
                  promoCodeApplied={promoCodeApplied}
                  promoCodeDiscount={promoCodeDiscount}
                  isApplyingPromo={isApplyingPromo}
                  showQuotationNumber={true}
                  showPromoCode={true}
                  className="!bg-transparent"
                  flowType={isSmartDevice ? "smartDevice" : "personalAccident"}
                />
              </div>
            </div>

            {/* Policy Notice */}
            <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse">
              <div className="flex-shrink-0 mt-0.5">
                <svg
                  className="w-5 h-5 text-[#A56213]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-[#252C32] font-semibold text-sm">
                  {translate(
                    "Kindly note that the policy will be issued under your name",
                    "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
                  )}
                </p>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mb-6">
              <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
                <label class="flex items-center cursor-pointer gap-2">
                  <input
                    type="checkbox"
                    class="peer hidden"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                  <div
                    class="size-5 border-2 border-gray-400 rounded-[4px] 
              peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7] 
              flex items-center justify-center transition-all"
                  >
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </label>
                {/* <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 size-5 text-[#00A9E7] bg-gray-100 border-gray-300 rounded focus:ring-[#00A9E7] focus:ring-2"
                /> */}
                <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px] text-justify">
                  <span>
                    {translate(
                      "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                      "أقر بموجب هذا بأن المعلومات المذكورة أعلاه صحيحة ودقيقة على حد علمي، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إلغاء وثيقة التأمين الخاصة بي. كما قرأت وأوافق على"
                    )}
                  </span>{" "}
                  <button
                    className="text-[#00384D] underline font-bold hover:text-[#0088CC]"
                    onClick={() =>
                      window.open(
                        isCyberInsurance
                            ? "https://content.solidarity.bh/uploads/Personal_Cyber_Insurance_0fcdb73314.pdf"
                            : "https://content.solidarity.bh/uploads/TRAVEL_INSURANCE_WORDING_30_08_2023_8e74afd369_44e9596879.pdf",
                        "_blank"
                      )
                    }
                  >
                    {translate("Policy Specification", "مواصفات وثيقة")}
                  </button>{" "}
                  <span>
                    {translate("of this insurance product.", "التأمين هذه.")}
                  </span>
                </div>
              </label>
            </div>

            {/* Proceed to Payment Button - Desktop */}
            <div className="max767:hidden">
              <Button
                onSubmit={handleProceedToPayment}
                disabled={!agreedToTerms || isLoading}
                isLoading={isLoading}
                label={translate("Proceed to Payment", "انتقل إلى الدفع")}
                post_icon={arrowForward}
                post_icon_class={isRTL ? "rotate-180 gap-2" : ""}
                className="w-full mt-6 py-4 px-6 rounded-[10px] gap-2"
              />
            </div>
          </div>
        </div>

        {/* Proceed to Payment Button - Mobile (Fixed at Bottom) */}
        <div className="hidden max767:block fixed bottom-0 left-0 w-full bg-white border-t border-[#E5E9EB] p-4 pb-6 z-[9] max767:pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Button
            onSubmit={handleProceedToPayment}
            disabled={!agreedToTerms || isLoading}
            isLoading={isLoading}
            label={translate("Proceed to Payment", "انتقل إلى الدفع")}
            post_icon={arrowForward}
            post_icon_class={isRTL ? "rotate-180" : ""}
            className="w-full py-4 px-6 rounded-[10px] h-12 text-sm"
            fontSize="text-sm"
          />
        </div>
      </LayoutComponent>
    </div>
  );
};

export default PaymentSummary;
